import React, { useState, useEffect } from 'react';
import {
  Bell,
  Check,
  CheckCheck,
  Clock,
  AlertTriangle,
  Wrench,
  Calendar,
  CreditCard,
  RefreshCw,
  Settings,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { notificationService } from '../../../services/notificationService';
import { useDateFormat } from '../../../hooks/useDateFormat';

const Notifications = () => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 20;
  const { formatRelativeTime } = useDateFormat();

  useEffect(() => {
    loadNotifications();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- helper identity changes each render; dependency list is intentional
  }, [currentPage]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationService.getNotifications(currentPage, pageSize);
      setNotifications(response.content || []);
      setTotalPages(response.totalPages || 0);
      setTotalElements(response.totalElements || 0);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(notifications.map(n =>
        n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
      ));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(notifications.map(n => ({
        ...n,
        isRead: true,
        readAt: new Date().toISOString()
      })));
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }
    if (notification.actionUrl) {
      window.location.href = notification.actionUrl;
    }
  };

  const getNotificationIcon = (type) => {
    const iconClass = "w-6 h-6";
    switch (type) {
      case 'WORK_ORDER_CREATED':
      case 'WORK_ORDER_ASSIGNED':
      case 'WORK_ORDER_UPDATED':
      case 'WORK_ORDER_COMPLETED':
        return <Wrench className={iconClass + " text-blue-500"} />;
      case 'WORK_ORDER_OVERDUE':
      case 'PM_SCHEDULE_OVERDUE':
      case 'COMPONENT_OVERDUE':
        return <AlertTriangle className={iconClass + " text-red-500"} />;
      case 'PM_SCHEDULE_DUE':
      case 'COMPONENT_DUE':
        return <Calendar className={iconClass + " text-orange-500"} />;
      case 'SUBSCRIPTION_TRIAL_ENDING':
      case 'SUBSCRIPTION_EXPIRED':
      case 'SUBSCRIPTION_RENEWED':
        return <CreditCard className={iconClass + " text-purple-500"} />;
      default:
        return <Bell className={iconClass + " text-gray-500"} />;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      WORK_ORDER_CREATED: 'Work Order',
      WORK_ORDER_ASSIGNED: 'Assignment',
      WORK_ORDER_UPDATED: 'Update',
      WORK_ORDER_COMPLETED: 'Completed',
      WORK_ORDER_OVERDUE: 'Overdue',
      PM_SCHEDULE_DUE: 'PM Due',
      PM_SCHEDULE_OVERDUE: 'PM Overdue',
      ASSET_DOWN: 'Asset Alert',
      ASSET_MAINTENANCE_DUE: 'Maintenance',
      COMPONENT_DUE: 'Component Due',
      COMPONENT_OVERDUE: 'Component Overdue',
      SUBSCRIPTION_TRIAL_ENDING: 'Trial Ending',
      SUBSCRIPTION_EXPIRED: 'Subscription',
      SUBSCRIPTION_RENEWED: 'Renewed',
      SYSTEM_ANNOUNCEMENT: 'Announcement',
      WELCOME: 'Welcome',
      ACCOUNT_UPDATE: 'Account',
      SECURITY_ALERT: 'Security',
    };
    return labels[type] || type;
  };

  const filteredNotifications = filter === 'unread'
    ? notifications.filter(n => !n.isRead)
    : notifications;

  const unreadCount = notifications.filter(n => !n.isRead).length;

  if (loading && currentPage === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('nav.notifications')}</h1>
          <p className="text-gray-600 mt-1">
            {totalElements} {totalElements !== 1 ? t('account.notifications') : t('account.notification')}
            {unreadCount > 0 && ` (${unreadCount} ${t('account.unread')})`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Filter */}
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 text-sm font-medium ${
                filter === 'all'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t('account.filterAll')}
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 text-sm font-medium ${
                filter === 'unread'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t('account.filterUnread')} {unreadCount > 0 && `(${unreadCount})`}
            </button>
          </div>

          {/* Mark All as Read */}
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="btn btn-outline flex items-center gap-2"
            >
              <CheckCheck className="w-4 h-4" />
              {t('account.markAllRead')}
            </button>
          )}

          {/* Settings Link */}
          <Link
            to="/portal/account/settings"
            className="btn btn-outline flex items-center gap-2"
          >
            <Settings className="w-4 h-4" />
            {t('common.settings')}
          </Link>

          {/* Refresh */}
          <button
            onClick={loadNotifications}
            className="btn btn-outline"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {filteredNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <Bell className="w-16 h-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">{t('account.noNotifications')}</h3>
            <p className="text-gray-500 text-sm">
              {filter === 'unread'
                ? t('account.allCaughtUp')
                : t('account.noNotificationsYet')}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`flex gap-4 p-4 transition-colors cursor-pointer ${
                  notification.isRead
                    ? 'bg-white hover:bg-gray-50'
                    : 'bg-blue-50/50 hover:bg-blue-50'
                }`}
              >
                {/* Icon */}
                <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                  notification.isRead ? 'bg-gray-100' : 'bg-white'
                }`}>
                  {getNotificationIcon(notification.type)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className={`text-sm ${notification.isRead ? 'text-gray-900' : 'text-gray-900 font-semibold'}`}>
                        {notification.title}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        {notification.message}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-gray-500 whitespace-nowrap flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatRelativeTime(notification.createdAt)}
                      </span>
                      {!notification.isRead && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notification.id);
                          }}
                          className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                          title="Mark as read"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      notification.type?.includes('OVERDUE') || notification.type?.includes('EXPIRED')
                        ? 'bg-red-100 text-red-800'
                        : notification.type?.includes('DUE') || notification.type?.includes('ENDING')
                        ? 'bg-yellow-100 text-yellow-800'
                        : notification.type?.includes('COMPLETED') || notification.type?.includes('RENEWED')
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {getTypeLabel(notification.type)}
                    </span>
                    {notification.actionUrl && (
                      <span className="text-xs text-primary-600 flex items-center gap-1">
                        {t('common.details')} <ChevronRight className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                </div>

                {/* Unread indicator */}
                {!notification.isRead && (
                  <div className="flex-shrink-0 self-center">
                    <div className="w-2.5 h-2.5 bg-primary-600 rounded-full"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600">
              Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                className="btn btn-outline btn-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
                className="btn btn-outline btn-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
